import React, { useState, useEffect } from 'react';
import AdminDashboard from './components/AdminDashboard';
import BillPage from './components/BillPage';
import QuotationPage from './components/QuotationPage';
import CustomerPage from './components/CustomerPage';
import ThemeToggle from './components/ThemeToggle';
import LoginPage from './components/LoginPage';
import LandingPage from './components/LandingPage';
import DynamicGreeting from './components/DynamicGreeting';
import { Button } from './components/ui/button';
import { Bell, LogOut } from 'lucide-react';
import { billAPI, authAPI } from './services/api';
import PendingPaymentsModal from './components/PendingPaymentsModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import AddUserModal from './components/AddUserModal';
import { Lock, UserPlus, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './components/ui/sheet';
import NotFound from './components/NotFound';
import { cn } from './lib/utils';
import { Package, FileText, FileSearch, Users as UsersIcon, LayoutDashboard, Receipt, FileStack, Users2 } from 'lucide-react';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import GlobalNotificationDisplay from './components/GlobalNotificationDisplay';
import { setNotificationHandlers } from './services/notificationService';
import useDisableNumberWheel from './hooks/useDisableNumberWheel';

const NotificationBridge = () => {
  const { showError, showSuccess } = useNotification();

  // Connect the Axios interceptor (notificationService) to the in-app toast system.
  // This is global and runs once per app lifetime; it doesn't change UI state directly.
  React.useEffect(() => {
    setNotificationHandlers({
      showError,
      showSuccess,
    });
  }, [showError, showSuccess]);

  return null;
};


function App() {
  useDisableNumberWheel();
  const [user, setUser] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const currTab = window.location.pathname;
  const validRoutes = ['/', '/dashboard', '/bills', '/quotations', '/customers'];
  const getInitialPage = (tab) => {
    switch (tab) {
      case '/':
      case '/dashboard': return 'dashboard';
      case '/bills': return 'bills';
      case '/quotations': return 'quotations';
      case '/customers': return 'customers';
      default: return 'notfound';
    }
  };
  const [currentPage, setCurrentPage] = useState(getInitialPage(currTab));

  const [pendingCount, setPendingCount] = useState(0);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showLogin, setShowLogin] = useState(window.location.pathname === '/staff-login' || window.location.pathname === '/login');
  const logo = require('./lib/logo.png');

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Failed to parse saved user', e);
        localStorage.removeItem('user');
      }
    }
    setIsInitializing(false);
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchPendingPayments = async () => {
      try {
        const response = await billAPI.getPendingPayments({
          pageSize: 20,
          pageNumber: 0
        });

        let count = 0;
        const responseData = response.data;

        // Handle common Spring Page response structure
        if (responseData.totalElements !== undefined) {
          count = responseData.totalElements;
        } else if (responseData.content && Array.isArray(responseData.content)) {
          count = responseData.content.length;
        } else if (Array.isArray(responseData)) {
          count = responseData.length;
        }

        setPendingCount(count);
      } catch (error) {
        console.error('Error fetching pending payments:', error);
      }
    };

    fetchPendingPayments();
    // Refresh every 5 minutes
    const interval = setInterval(fetchPendingPayments, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#001524]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (!user) {
    if (showLogin) {
      return <LoginPage onLoginSuccess={handleLoginSuccess} onBack={() => setShowLogin(false)} />;
    }
    return <LandingPage onLoginClick={() => setShowLogin(true)} />;
  }

  return (
    <NotificationProvider>
      <NotificationBridge />
      <div className="min-h-screen">
      <nav className="sticky top-0 z-50 w-full bg-white dark:bg-zinc-950 backdrop-blur-md border-b-4 border-[#FF7D00] shadow-sm">
        <div className="w-full flex min-h-16 py-2 items-center justify-between px-4 lg:px-6 gap-4">
          {/* Mobile Layout: Logo, Greeting, Actions Menu */}
          <div className="flex lg:hidden items-center justify-between w-full gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-500 rounded-lg shrink-0">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[350px] bg-white dark:bg-zinc-950 border-r border-[#FF7D00]">
                  {/* ... (Mobile Drawer Content same as before) */}
                  <div className="flex flex-col gap-6 mt-6">
                    <div className="flex items-center gap-3 px-2">
                      <div className="p-2 bg-[#FF7D00]/10 rounded-xl">
                        <img src={logo} alt="logo" className="w-8 h-8" />
                      </div>
                      <span className="text-xl font-black text-zinc-900 dark:text-white tracking-tight">JIT</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <SheetClose asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            "justify-start text-lg h-12 rounded-xl transition-all duration-200",
                            currentPage === 'dashboard' ? 'bg-[#FF7D00] text-white shadow-lg shadow-orange-500/20' : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800'
                          )}
                          onClick={() => {
                            window.history.pushState(null, '', '/dashboard');
                            setCurrentPage('dashboard');
                          }}
                        >
                          <LayoutDashboard className="mr-3 h-5 w-5" />
                          Dashboard
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            "justify-start text-lg h-12 rounded-xl transition-all duration-200",
                            currentPage === 'bills' ? 'bg-[#FF7D00] text-white shadow-lg shadow-orange-500/20' : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800'
                          )}
                          onClick={() => {
                            window.history.pushState(null, '', '/bills');
                            setCurrentPage('bills');
                          }}
                        >
                          <Receipt className="mr-3 h-5 w-5" />
                          Bills
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            "justify-start text-lg h-12 rounded-xl transition-all duration-200",
                            currentPage === 'quotations' ? 'bg-[#FF7D00] text-white shadow-lg shadow-orange-500/20' : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800'
                          )}
                          onClick={() => {
                            window.history.pushState(null, '', '/quotations');
                            setCurrentPage('quotations');
                          }}
                        >
                          <FileStack className="mr-3 h-5 w-5" />
                          Quotations
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            "justify-start text-lg h-12 rounded-xl transition-all duration-200",
                            currentPage === 'customers' ? 'bg-[#FF7D00] text-white shadow-lg shadow-orange-500/20' : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800'
                          )}
                          onClick={() => {
                            window.history.pushState(null, '', '/customers');
                            setCurrentPage('customers');
                          }}
                        >
                          <Users2 className="mr-3 h-5 w-5" />
                          Customers
                        </Button>
                      </SheetClose>
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-6 right-6 border-t border-zinc-100 dark:border-zinc-800 pt-6">
                    <div className="mb-4">
                      <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest">Signed in as</p>
                      <p className="text-zinc-900 dark:text-white font-black truncate">
                        {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : (user.firstName || user.username)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        className="justify-start text-zinc-500 hover:text-zinc-900 dark:hover:text-white rounded-xl h-11"
                        onClick={() => setShowChangePasswordModal(true)}
                      >
                        <Lock className="mr-3 h-5 w-5" /> Change Password
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl h-11"
                        onClick={handleLogout}
                      >
                        <LogOut className="mr-3 h-5 w-5" /> Logout
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div className="flex items-center gap-2 min-w-0">
                <img src={logo} alt="logo" className="w-6 h-6 shrink-0" />
                <div className="h-5 w-px bg-zinc-200 dark:bg-zinc-800 shrink-0"></div>
                <DynamicGreeting isMobile={true} />
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0 bg-zinc-50 dark:bg-zinc-900/50 p-1 rounded-lg border border-zinc-100 dark:border-zinc-800">
               <ThemeToggle />
               <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-400 hover:text-[#FF7D00] transition-colors"
                onClick={() => setShowAddUserModal(true)}
              >
                <UserPlus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-400 hover:text-[#FF7D00] transition-colors"
                onClick={() => setShowChangePasswordModal(true)}
              >
                <Lock className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500/70 hover:text-red-600 transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Desktop Layout: Branding & Greeting */}
          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#FF7D00]/10 rounded-xl">
                  <img src={logo} alt="logo" className="w-8 h-8" />
                </div>
                <span className="text-xl font-black text-zinc-900 dark:text-white tracking-tight">JIT</span>
              </div>
              <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-800 mx-2"></div>
              <DynamicGreeting />
            </div>
          </div>

          {/* Desktop Navigation Links - Visible only on LG+ */}
          <div className="hidden lg:flex items-center justify-center gap-1.5 bg-zinc-100/50 dark:bg-zinc-900/50 p-1.5 rounded-xl border border-zinc-200/50 dark:border-zinc-800/50 flex-1 max-w-2xl mx-4">
            <ThemeToggle />
            <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
            <Button
              variant="ghost"
              className={cn(
                "px-5 h-10 font-bold rounded-lg transition-all duration-300",
                currentPage === 'dashboard' 
                  ? "bg-[#FF7D00] text-white shadow-lg shadow-orange-500/30" 
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 border border-transparent"
              )}
              onClick={() => { window.history.pushState(null, '', '/dashboard'); setCurrentPage('dashboard'); }}
            >
              <LayoutDashboard className={cn("h-4 w-4 mr-2", currentPage === 'dashboard' ? "block" : "hidden sm:block")} />
              <span className="hidden xl:inline">Dashboard</span>
              <span className="xl:hidden">DB</span>
            </Button>

            <Button
              variant="ghost"
              className={cn(
                "px-5 h-10 font-bold rounded-lg transition-all duration-300",
                currentPage === 'bills' 
                  ? "bg-[#FF7D00] text-white shadow-lg shadow-orange-500/30" 
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 border border-transparent"
              )}
              onClick={() => { window.history.pushState(null, '', '/bills'); setCurrentPage('bills'); }}
            >
              <Receipt className={cn("h-4 w-4 mr-2", currentPage === 'bills' ? "block" : "hidden sm:block")} />
              <span className="hidden xl:inline">Bills</span>
              <span className="xl:hidden">Bills</span>
            </Button>

            <Button
              variant="ghost"
              className={cn(
                "px-5 h-10 font-bold rounded-lg transition-all duration-300",
                currentPage === 'quotations' 
                  ? "bg-[#FF7D00] text-white shadow-lg shadow-orange-500/30" 
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 border border-transparent"
              )}
              onClick={() => { window.history.pushState(null, '', '/quotations'); setCurrentPage('quotations'); }}
            >
              <FileStack className={cn("h-4 w-4 mr-2", currentPage === 'quotations' ? "block" : "hidden sm:block")} />
              <span className="hidden xl:inline">Quotations</span>
              <span className="xl:hidden">Quotes</span>
            </Button>

            <Button
              variant="ghost"
              className={cn(
                "px-5 h-10 font-bold rounded-lg transition-all duration-300",
                currentPage === 'customers' 
                  ? "bg-[#FF7D00] text-white shadow-lg shadow-orange-500/30" 
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 border border-transparent"
              )}
              onClick={() => { window.history.pushState(null, '', '/customers'); setCurrentPage('customers'); }}
            >
              <Users2 className={cn("h-4 w-4 mr-2", currentPage === 'customers' ? "block" : "hidden sm:block")} />
              <span className="hidden xl:inline">Customers</span>
              <span className="xl:hidden">Cust</span>
            </Button>
          </div>

          <div className="hidden lg:flex items-center gap-4 shrink-0">

            {/* Desktop User Actions (Icons + Greeting) */}
            <div className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-900/50 px-4 py-1.5 rounded-xl border border-zinc-100 dark:border-zinc-800">
              <span className='hidden lg:inline text-zinc-500 dark:text-zinc-400 font-medium text-sm whitespace-nowrap'>
                   Hello, <span className="text-zinc-900 dark:text-white font-bold">{user.firstName || user.username}</span>
              </span>
              
              <div className="hidden lg:block h-4 w-px bg-zinc-200 dark:bg-zinc-800 mx-1"></div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-[#FF7D00] hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-all"
                  onClick={() => setShowChangePasswordModal(true)}
                  title="Change Password"
                >
                  <Lock className="h-4 w-4" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-[#FF7D00] hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-all"
                  onClick={() => setShowAddUserModal(true)}
                  title="Add User"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                  onClick={handleLogout}
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Global Navigation Belt - Scrollable on Mobile */}
        <div className="w-full px-4 pb-2 lg:hidden">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
            
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "px-4 h-9 font-bold rounded-lg shrink-0 transition-all",
                currentPage === 'dashboard' 
                  ? "bg-[#FF7D00] text-white shadow-md shadow-orange-500/20" 
                  : "text-zinc-500 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800"
              )}
              onClick={() => { window.history.pushState(null, '', '/dashboard'); setCurrentPage('dashboard'); }}
            >
              Dashboard
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "px-4 h-9 font-bold rounded-lg shrink-0 transition-all",
                currentPage === 'bills' 
                  ? "bg-[#FF7D00] text-white shadow-md shadow-orange-500/20" 
                  : "text-zinc-500 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800"
              )}
              onClick={() => { window.history.pushState(null, '', '/bills'); setCurrentPage('bills'); }}
            >
              Bills
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "px-4 h-9 font-bold rounded-lg shrink-0 transition-all",
                currentPage === 'quotations' 
                  ? "bg-[#FF7D00] text-white shadow-md shadow-orange-500/20" 
                  : "text-zinc-500 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800"
              )}
              onClick={() => { window.history.pushState(null, '', '/quotations'); setCurrentPage('quotations'); }}
            >
              Quotes
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "px-4 h-9 font-bold rounded-lg shrink-0 transition-all",
                currentPage === 'customers' 
                  ? "bg-[#FF7D00] text-white shadow-md shadow-orange-500/20" 
                  : "text-zinc-500 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800"
              )}
              onClick={() => { window.history.pushState(null, '', '/customers'); setCurrentPage('customers'); }}
            >
              Customers
            </Button>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-6">
        {currentPage === 'dashboard' && <AdminDashboard
          pendingCount={pendingCount}
          onShowPendingModal={() => setShowPendingModal(true)}
        />}
        {currentPage === 'bills' && <BillPage onBack={() => {
          window.history.pushState(null, '', '/dashboard');
          setCurrentPage('dashboard');
        }}
        />}
        {currentPage === 'quotations' && <QuotationPage onBack={() => {
          window.history.pushState(null, '', '/dashboard');
          setCurrentPage('dashboard');
        }}
        />}
        {currentPage === 'customers' && <CustomerPage onBack={() => {
          window.history.pushState(null, '', '/dashboard');
          setCurrentPage('dashboard');
        }}
        />}
        {currentPage === 'notfound' && <NotFound onBack={() => {
          window.history.pushState(null, '', '/dashboard');
          setCurrentPage('dashboard');
        }} />}
      </main>


      <PendingPaymentsModal
        open={showPendingModal}
        onOpenChange={setShowPendingModal}
      />

      {showChangePasswordModal && (
        <ChangePasswordModal
          username={user?.username}
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            alert("Password updated successfully!");
          }}
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onSuccess={(username) => {
            alert(`User "${username}" added successfully! Please proceed to assign role for registered user.`);
            setShowAddUserModal(false);
          }}
        />
      )}
      </div>
      <GlobalNotificationDisplay />
    </NotificationProvider>
  );
}

export default App;
